// 🧊🗂 모션 무대 맵 — 무대 종류(`MotionStageKind`) → 그리는 컴포넌트 한 쌍(들어오는 무대 · 나가는 무대).
//   표(스킨 → 무대 종류)는 `src/lib/templates/motion-stage-family.ts`(순수 데이터)에 있고, 컴포넌트는 여기에만 둔다
//   (lib 이 remotion 컴포넌트를 import 하지 않게). 장표 층(`Overlay169SlideLayer`)이 이 맵에서 꺼내 끼운다.
import type React from 'react';
import type { MotionStageKind } from '../../lib/templates/motion-stage-family';
import { MotionBriefExit, MotionBriefStage, type BriefCaption, type MotionBriefStageProps } from './MotionBriefStage';
import { briefContentColors, briefFillOf, type BriefSlide } from './motion-brief-shape';
import { ColorCutExit, ColorCutStage } from './ColorCutStage';
import { colorCutContentColors, colorCutPalette, colorCutScene } from './color-cut-palette';
import type { AtmosphereColors } from './tokens';
import { ShowreelExit, ShowreelStage } from './ShowreelStage';
import { reelContentColors, reelFillOf } from './showreel-tokens';

/** 무대 공용 props — 모든 무대가 같은 것을 받는다(안 쓰는 값은 무시). */
export interface MotionStageProps extends MotionBriefStageProps {
  /** 전체 씬 번호(0부터) — 이웃 장면과 다른 색을 고르는 무대가 쓴다. 모션 브리핑 무대는 쓰지 않는다. */
  sceneIndex?: number;
}

/** 나가는 무대 공용 props — 장표 장면 다음이 영상·그림 장면일 때 캔버스가 빠지는 층. */
export interface MotionStageExitProps {
  f: number;
  prevSlide: BriefSlide;
  canvas: string;
  brand: string;
  reserveRightPx?: number;
  prevCaption?: BriefCaption;
  split?: boolean;
  /** 지금(영상·그림) 장면의 전체 씬 번호 — 빠지는 장표는 `sceneIndex - 1`. */
  sceneIndex?: number;
}

export interface MotionStagePair {
  Stage: React.ComponentType<MotionStageProps>;
  Exit: React.ComponentType<MotionStageExitProps>;
  /** 무대 안 장표가 쓸 색 — 장표 종류·스킨 장표 색(브랜드색 보정 뒤)·도형 브랜드색·씬 번호로 정한다. */
  contentColors: (slideType: string, colors: AtmosphereColors, brand: string, sceneIndex?: number) => AtmosphereColors;
}

export const MOTION_STAGES: Record<MotionStageKind, MotionStagePair> = {
  brief: {
    Stage: MotionBriefStage,
    Exit: MotionBriefExit,
    // 포스터형은 사진 위 어둠 깔개에 글자를 얹는다 → 흰 글자. 나머지는 도형 색(종이·브랜드·먹)에 맞춘 글자색.
    contentColors: (slideType, colors, brand) => (slideType === 'posterStack'
      ? { ...colors, textMain: '#FFFFFF', textSub: '#E5E7EB', isDark: true }
      : briefContentColors(briefFillOf(slideType), colors, brand)),
  },
  showreel: {
    Stage: ShowreelStage,
    Exit: ShowreelExit,
    // 포스터형은 사진 위 어둠 깔개 → 흰 글자. 나머지는 판 색(짙은 판·원색 판)에 맞춘 글자색.
    contentColors: (slideType, colors, brand) => (slideType === 'posterStack'
      ? { ...colors, textMain: '#FFFFFF', textSub: '#E5E7EB', isDark: true }
      : reelContentColors(reelFillOf(slideType), colors, brand)),
  },  colorcut: {
    Stage: ColorCutStage,
    Exit: ColorCutExit,
    // 장면색(브랜드색에서 뽑은 4색 중 이 씬 번호의 색) 위에서 대비 큰 글자색. 포스터형은 사진 위라 흰 글자.
    contentColors: (slideType, colors, brand, sceneIndex) => (slideType === 'posterStack'
      ? { ...colors, textMain: '#FFFFFF', textSub: '#E5E7EB', isDark: true }
      : colorCutContentColors(colors, colorCutScene(colorCutPalette(brand), { sceneIndex, type: slideType }).color)),
  },
};
